import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getTtfMetrics } from '../../services/adminService';
import { formatPrice } from '../../utils/format';

const PAYMENT_LABELS = {
  mercado_pago: 'Tarjeta (liga de Mercado Pago)',
  card: 'Tarjeta (Stripe, anterior)',
  bank_transfer: 'Transferencia 50% (retirada)',
  bank_transfer_full: 'Transferencia bancaria',
  unknown: 'Sin detalle',
};

const card = {
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 12,
  padding: '1.25rem 1.5rem',
};

const sectionTitle = { fontSize: '1rem', color: '#333', margin: '0 0 0.75rem' };

const StatCard = ({ label, value, sub }) => (
  <div style={{ ...card, minWidth: 150, flex: '1 1 150px' }}>
    <div style={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#888', marginBottom: '0.4rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: '0.78rem', color: '#999', marginTop: '0.3rem' }}>{sub}</div>}
  </div>
);

const th = { padding: '0.6rem 0.9rem', textAlign: 'left', color: '#666', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0', fontSize: '0.85rem' };
const td = { padding: '0.6rem 0.9rem', borderBottom: '1px solid #f8f8f8', fontSize: '0.88rem' };

const AdminTtfMetricsPage = () => {
  const { session } = useAuth();
  const token = session?.access_token;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getTtfMetrics(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const fmt = (n) => Number(n || 0).toLocaleString('es-MX');
  const mxn = (n) => formatPrice(Number(n || 0), 'MXN', 'es-MX');
  const maxFunnel = data ? Math.max(1, ...data.funnel.map((f) => f.sessions)) : 1;

  return (
    <div className="container" style={{ padding: '2rem 0 5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <Link to="/admin/ttf-pieces" style={{ fontSize: '0.82rem', color: '#888', textDecoration: 'none' }}>← Pedidos Taipei Toy Festival</Link>
          <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: '0.25rem 0 0' }}>
            Métricas Taipei Toy Festival
          </h1>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{ fontSize: '0.8rem', color: '#888', background: 'none', border: '1px solid #e5e5e5', borderRadius: 6, padding: '0.35rem 0.7rem', cursor: 'pointer' }}
        >
          {loading ? 'Actualizando…' : '↻ Actualizar'}
        </button>
      </div>

      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {loading && !data && <p style={{ color: '#888' }}>Cargando métricas…</p>}

      {data && (
        <>
          {/* Money + orders — from the real orders */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
            <StatCard label="Pedidos" value={fmt(data.totals.orders)} sub={data.totals.cancelledOrders ? `${fmt(data.totals.cancelledOrders)} cancelados aparte` : null} />
            <StatCard label="Monto comprometido" value={mxn(data.totals.committed)} sub={`Promedio ${mxn(data.totals.averageOrder)}`} />
            <StatCard label="Cobrado" value={mxn(data.totals.collected)} sub={`Por cobrar ${mxn(data.totals.pending)}`} />
            <StatCard
              label="Conversión"
              value={data.totals.conversionRate == null ? '—' : `${data.totals.conversionRate}%`}
              sub={`${fmt(data.totals.visits)} visitas`}
            />
          </div>

          {/* Funnel — from page events */}
          <section style={{ ...card, marginBottom: '1.5rem' }}>
            <h2 style={sectionTitle}>Embudo de pedido</h2>
            {data.funnel.map((f) => (
              <div key={f.step} data-testid={`funnel-${f.step}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 220px) 1fr 110px', gap: '0.75rem', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.88rem', color: '#444' }}>{f.label}</span>
                <div style={{ background: '#f3f3f0', borderRadius: 6, height: 18, overflow: 'hidden' }}>
                  <div style={{ width: `${(f.sessions / maxFunnel) * 100}%`, height: '100%', background: '#E4405F', borderRadius: 6 }} />
                </div>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, textAlign: 'right' }}>
                  {fmt(f.sessions)}{f.rate != null && f.step !== 'view' ? ` · ${f.rate}%` : ''}
                </span>
              </div>
            ))}
            <p style={{ fontSize: '0.78rem', color: '#999', margin: '0.75rem 0 0' }}>
              Visitas únicas por paso (solo producción; los bloqueadores de anuncios pueden ocultar algunas, así que es un mínimo).
              Calculadora usada en {fmt(data.totals.calculatorSessions)} visitas.
            </p>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* Payment methods */}
            <section style={card}>
              <h2 style={sectionTitle}>Por método de pago</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Método</th><th style={th}>Pedidos</th><th style={th}>Comprometido</th><th style={th}>Cobrado</th></tr></thead>
                <tbody>
                  {Object.keys(data.byPaymentMethod).length === 0 ? (
                    <tr><td style={{ ...td, color: '#888' }} colSpan={4}>Sin pedidos aún.</td></tr>
                  ) : Object.entries(data.byPaymentMethod).map(([method, m]) => (
                    <tr key={method}>
                      <td style={td}>{PAYMENT_LABELS[method] || method}</td>
                      <td style={td}>{fmt(m.orders)}</td>
                      <td style={td}>{mxn(m.committed)}</td>
                      <td style={td}>{mxn(m.collected)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {/* Payment status + delivery + attention */}
            <section style={card}>
              <h2 style={sectionTitle}>Estado</h2>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr><td style={td}>Pagados al 100%</td><td style={{ ...td, fontWeight: 600 }}>{fmt(data.byPaymentStatus.paid)}</td></tr>
                  <tr><td style={td}>Con anticipo</td><td style={{ ...td, fontWeight: 600 }}>{fmt(data.byPaymentStatus.partial)}</td></tr>
                  <tr><td style={td}>Sin pago</td><td style={{ ...td, fontWeight: 600 }}>{fmt(data.byPaymentStatus.unpaid)}</td></tr>
                  <tr><td style={td}>Recoger en tienda / Envío</td><td style={{ ...td, fontWeight: 600 }}>{fmt(data.byDelivery.pickup)} / {fmt(data.byDelivery.delivery)}</td></tr>
                  <tr>
                    <td style={td}>Ligas de Mercado Pago sin pagar</td>
                    <td style={{ ...td, fontWeight: 600, color: data.totals.awaitingMercadoPago ? '#f59e0b' : undefined }}>{fmt(data.totals.awaitingMercadoPago)}</td>
                  </tr>
                  <tr>
                    <td style={td}>Errores al enviar el pedido</td>
                    <td style={{ ...td, fontWeight: 600, color: data.totals.failedSubmissions ? '#dc3545' : undefined }}>{fmt(data.totals.failedSubmissions)}</td>
                  </tr>
                </tbody>
              </table>
              {data.topErrors.length > 0 && (
                <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1.1rem', fontSize: '0.8rem', color: '#666' }}>
                  {data.topErrors.map((e) => <li key={e.message}>{e.message} ({fmt(e.count)})</li>)}
                </ul>
              )}
            </section>
          </div>

          {/* Piece demand */}
          <section style={{ ...card, marginBottom: '1.5rem', padding: 0, overflow: 'hidden' }}>
            <h2 style={{ ...sectionTitle, padding: '1.25rem 1.5rem 0' }}>Demanda por pieza</h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Pieza', 'Precio', 'Agregada', 'Pedida', 'Firma', 'Lotería (ganada / no / pendiente)'].map((h) => <th key={h} style={th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.pieces.length === 0 ? (
                    <tr><td style={{ ...td, color: '#888' }} colSpan={6}>Sin actividad aún.</td></tr>
                  ) : data.pieces.map((p) => (
                    <tr key={p.id}>
                      <td style={td}>{p.name}{p.isSoldOut ? <span style={{ color: '#dc3545', fontSize: '0.75rem' }}> · agotada</span> : null}</td>
                      <td style={td}>{p.priceMxn == null ? 'Sin precio' : mxn(p.priceMxn)}</td>
                      <td style={td}>{fmt(p.added)}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{fmt(p.ordered)}</td>
                      <td style={td}>{fmt(p.signatures)}</td>
                      <td style={td}>{p.isLottery ? `${fmt(p.lotteryWon)} / ${fmt(p.lotteryLost)} / ${fmt(p.lotteryPending)}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Requests */}
          <section style={card}>
            <h2 style={sectionTitle}>Solicitudes de piezas</h2>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#444' }}>
              {fmt(data.requests.total)} solicitudes · {fmt(data.requests.new)} nuevas · {fmt(data.requests.added)} agregadas al catálogo ·{' '}
              {fmt(data.requests.dismissed)} descartadas · {fmt(data.requests.withSignature)} quieren firma
            </p>
          </section>
        </>
      )}
    </div>
  );
};

export default AdminTtfMetricsPage;
